import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Grid from '@mui/material/Grid'
import Text from '../../../components/Text'
import { colors } from '../../../styles/_var'
import MoneyIcon from '@mui/icons-material/LocalAtmRounded'
import { TBuckBreaker } from '../../../utils/types'
import { pluralize, humanize } from '../../../utils/helpers'

type BuckBreakerListProps = {
  buckbreakers: TBuckBreaker[]
  onBuckBreakerClick: (friendlyId: string) => void
}

export const BuckBreakerList = ({ buckbreakers, onBuckBreakerClick }: BuckBreakerListProps) => {
  return (
    <Grid container spacing={2}>
      {buckbreakers.map((buckbreaker) => (
        <Grid item xs={12} sm={6} md={4} key={buckbreaker.friendly_id}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 4,
              },
            }}
            onClick={() => onBuckBreakerClick(buckbreaker.friendly_id)}
          >
            <Box display="flex" alignItems="center" mb={1}>
              <MoneyIcon sx={{ mr: 1, color: colors.primary }} />
              <Text variant="medium" weight="bold" useDiv>
                {buckbreaker.title}
              </Text>
            </Box>
            {buckbreaker.description && (
              <Text variant="regular" color={colors.darkgrey} useDiv>
                {buckbreaker.description}
              </Text>
            )}
            {(buckbreaker.buck_breaker_participants?.length > 0 || buckbreaker.created_at) && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                {buckbreaker.buck_breaker_participants?.length > 0 && (
                  <Text variant="small" useDiv>
                    {buckbreaker.buck_breaker_participants.length} {pluralize('participant', buckbreaker.buck_breaker_participants.length)}
                  </Text>
                )}
                {buckbreaker.created_at && (
                  <Text variant="small" color={colors.darkgrey} useDiv>
                    {humanize(buckbreaker.created_at, true)}
                  </Text>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      ))}
    </Grid>
  )
}

type BuckBreakerLoadingStateProps = {
  count?: number
}

export const BuckBreakerLoadingState = ({ count = 3 }: BuckBreakerLoadingStateProps) => {
  return (
    <Grid container spacing={2}>
      {Array(count).fill(0).map((_, i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rectangular" height={150} />
        </Grid>
      ))}
    </Grid>
  )
}

export const BuckBreakerEmptyState = () => {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        textAlign: 'center',
        border: `dashed 1px ${colors.darkgrey}`,
      }}
    >
      <MoneyIcon sx={{ fontSize: 48, color: colors.darkgrey, mb: 2 }} />
      <Text variant="medium" weight="semibold" useDiv>
        No Buck Breakers yet
      </Text>
      <Text variant="small" color={colors.darkgrey} mt={1} useDiv>
        Create your first Buck Breaker to start tracking expenses
      </Text>
    </Paper>
  )
}
